import { defineMessages } from 'react-intl';

export const messages = defineMessages({
  panelTitle: {
    id: 'ZMxdEp',
    defaultMessage: 'Packing Recommendations',
    description: 'Title of the packing recommendations panel shown in all its states',
  },
  recommendationApplied: {
    id: '4MVsWk',
    defaultMessage: 'Packing Recommendations Applied!',
    description: 'Title shown after a packing recommendation has been applied to the shipment',
  },
  calculationFailed: {
    id: 'jY2IBU',
    defaultMessage: 'Calculation failed. Please try again.',
    description: 'Error text shown when the packing recommendation calculation fails with no specific reason',
  },
  calculationTimedOut: {
    id: 'Sk1/y1',
    defaultMessage: 'Timed Out. Please try again.',
    description: 'Error text shown when the packing recommendation calculation times out',
  },
  errorConnectionTitle: {
    id: 'GlziuP',
    defaultMessage: 'Connection Error',
    description: 'Title shown when the packing recommendation calculation fails due to a network error',
  },
  calculationNetworkError: {
    id: 'wxiBtx',
    defaultMessage: 'Please check your connection and try again.',
    description: 'Error text shown when the packing recommendation calculation fails due to a network error',
  },
  calculationServiceUnavailable: {
    id: 'qCT7uI',
    defaultMessage: 'Service Unavailable. Please try again later.',
    description: 'Error text shown when the packing service returns 503',
  },
  calculationRateLimited: {
    id: 'wuX8ij',
    defaultMessage: 'Too Many Requests. Please try again.',
    description: 'Error text shown when the packing service returns 429 rate limit',
  },
  errorNoSolutionTitle: {
    id: 'M5kLV4',
    defaultMessage: 'No Solution Found',
    description: 'Title shown when the packing algorithm cannot find a valid packing solution',
  },
  calculationNoSolutionFound: {
    id: 'mQxBgE',
    defaultMessage: 'Try adjusting item dimensions or adding more custom packages with correct dimensions and max weights.',
    description: 'Error text shown when the packing algorithm cannot find a valid packing solution',
  },
  cancel: {
    id: 'Jtqud3',
    defaultMessage: 'Cancel',
    description: 'Label for cancel button on the packing recommendation card',
  },
  run: {
    id: '805T83',
    defaultMessage: 'Run',
    description: 'Label for the small action button that triggers the packing recommendation calculation from the collapsed panel',
  },
  retry: {
    id: 'bs6iRW',
    defaultMessage: 'Retry',
    description: 'Label for the button that retries the packing recommendation calculation after an error',
  },
  reject: {
    id: 'BvjX+E',
    defaultMessage: 'Reject',
    description: 'Label for the button that dismisses the packing recommendation without applying it',
  },
  clear: {
    id: 'cS47+0',
    defaultMessage: 'Clear',
    description: 'Label for the button that clears the applied packing recommendation',
  },
  manageSettings: {
    id: '1UkVLX',
    defaultMessage: 'Manage Settings',
    description: 'Link that navigates to the custom packages settings page from the packing recommendations panel',
  },
  expandPackaging: {
    id: 'VksLLJ',
    defaultMessage: 'Show package list',
    description: 'Aria label for the button that expands the applied packing recommendation package list',
  },
  collapsePackaging: {
    id: 'MsuXTQ',
    defaultMessage: 'Hide package list',
    description: 'Aria label for the button that collapses the applied packing recommendation package list',
  },
  dimensionFormat: {
    id: 'yH1s1v',
    defaultMessage: '{length}{unit} × {width}{unit} × {height}{unit}',
    description: 'Formatted box dimensions with unit symbol (e.g. 12" x 8" x 6")',
  },
  unitInches: {
    id: 'KP9oU/',
    defaultMessage: '"',
    description: 'Unit symbol for inches measurement',
  },
  unitCentimeters: {
    id: 'jp3R7Y',
    defaultMessage: ' cm',
    description: 'Unit symbol for centimeters measurement',
  },
  validationFailed: {
    id: 'PXhmi3',
    defaultMessage: 'Unable to calculate: please ensure items have dimensions and custom packages are available.',
    description: 'Error notification shown when packing validation fails before calculation',
  },
  packageConfigured: {
    id: 'Pp8+AJ',
    defaultMessage: '{name} configured',
    description: 'Text showing the name of the single package configured after applying a recommendation',
  },
  packagesConfigured: {
    id: 'WcdtRO',
    defaultMessage: '{count, plural, one {# Package} other {# Packages}} configured',
    description: 'Text showing how many packages were configured after applying a recommendation',
  },
  rerun: {
    id: 'RBj+V1',
    defaultMessage: 'Rerun',
    description: 'Link label to re-run the package recommendation calculation',
  },
  makingRecommendation: {
    id: 'BiEVo1',
    defaultMessage: 'Making Package Recommendations',
    description: 'Text shown while the AI is calculating the package recommendation',
  },
  apply: {
    id: 'l8ghmh',
    defaultMessage: 'Apply',
    description: 'Label for button that applies the package recommendation',
  },
  andMorePackages: {
    id: 'S5BQpd',
    defaultMessage: 'and {count, plural, one {# more package} other {# more packages}}',
    description: 'Text showing how many additional packages are not displayed in the list',
  },
  allItemsGoInto: {
    id: 'aLxo9U',
    defaultMessage: 'All items go into <bold>{packageName}</bold>',
    description: 'Single-package recommendation: shows which package all items should be placed in',
  },
  packageTitle: {
    id: 'GDrstK',
    defaultMessage: 'Package {number} - {packageName}',
    description: 'Title of one package group in the packing recommendation list',
  },
  itemEntry: {
    id: 'x35Nc3',
    defaultMessage: '{itemName} - {count}',
    description: 'One item in a multi-package box list, with item name and quantity',
  },
  missingItemDimensionsWarning: {
    id: 'ker7U8',
    defaultMessage: 'Packing recommendations not applied. Add the missing product dimensions.',
    description: 'Warning banner text shown when an order item is missing the dimensions needed to calculate a packing recommendation',
  },
  missingCustomPackagesWarning: {
    id: 'HDFTZk',
    defaultMessage: 'Packing recommendations not applied. Update your custom packages.',
    description: 'Warning banner text shown when no custom packages with valid dimensions and max weight are configured',
  },
  updatePackages: {
    id: 'WLVFcF',
    defaultMessage: 'Update Packages',
    description: 'Button label that navigates to the custom packages settings page from the packing recommendation warning banner',
  },
  statusCardDismissedTitle: {
    id: 'bsNep3',
    defaultMessage: 'Packaging Recommendation Dismissed',
    description: 'Title shown on the packing recommendation status card when the last run was dismissed',
  },
  statusCardFailedTitle: {
    id: '6P43Eu',
    defaultMessage: 'Packaging Recommendation Failed',
    description: 'Title shown on the packing recommendation status card when the last run failed',
  },
  statusCardStaleTitle: {
    id: 'xv1tMv',
    defaultMessage: 'Packing Recommendation is stale',
    description:
      'Warning shown on the packing recommendation status card when a previously applied recommendation is stale due to item changes',
  },
  failureReasonNoItems: {
    id: 'xFzBIu',
    defaultMessage: 'Packing recommendations not applied. The order has no valid items to pack.',
    description: 'Failure reason text shown on the status card when the order had no items',
  },
  failureReasonNoValidPackage: {
    id: 'iinm86',
    defaultMessage: 'No compatible package found for these items.',
    description: 'Failure reason text shown on the status card when no valid package could be found',
  },
  failureReasonOrderAlreadyShipped: {
    id: 'VZNHrl',
    defaultMessage: 'This order has already shipped.',
    description: 'Failure reason text shown on the status card when the order was already shipped or cancelled',
  },
  failureReasonPackingAiServiceError: {
    id: 'q2AYwY',
    defaultMessage: 'The packing service ran into an error.',
    description: 'Failure reason text shown on the status card when the packing AI service returned an error',
  },
  failureReasonTimeout: {
    id: '/prKL4',
    defaultMessage: 'The calculation timed out.',
    description: 'Failure reason text shown on the status card when the calculation timed out',
  },
  failureReasonUnknown: {
    id: '9t9Qk5',
    defaultMessage: 'Something went wrong.',
    description: 'Failure reason text shown on the status card for an unrecognized failure reason',
  },
});
